import { Controller } from 'stimulus';
import csrfToken from '../helpers/csrfToken';

const prevent = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

class FileUpload extends Controller {
  connect() {
    this.files = {};
    this.filesToUpload = [];
    this.errors = [];

    this.name = this.data.get('name');
    this.extensions = this.data.get('extensions').split(',');
    this.maxFiles = parseInt(this.data.get('max-files'), 10);
    this.maxFileSizeInKb = parseInt(this.data.get('max-filesize'), 10);
    this.maxFileSizeInMb = Math.round(this.maxFileSizeInKb / 1024);
  }

  onDrop(event) {
    prevent(event);

    this.errors = [];
    this.renderErrors();

    this.toggleDragClass(false);

    this.filesToUpload = Array.prototype.slice.call(event.dataTransfer.files);

    if (this.validateFiles()) {
      this.uploadFiles();
    }
  }

  onFileAddByInput(event) {
    this.errors = [];
    this.renderErrors();

    this.filesToUpload = Array.prototype.slice.call(event.currentTarget.files);

    if (this.validateFiles()) {
      this.uploadFiles();
    }
  }

  onRemoveFile(event) {
    const { currentTarget } = event;
    const key = currentTarget.getAttribute('data-key');

    Object.keys(this.files).forEach((fileName) => {
      if (fileName === key) {
        this.deleteFile(this.files[fileName]);
        delete this.files[fileName];
      }
    });

    this.renderFiles();
  }

  validateFiles() {
    const currentFilesCount = Object.keys(this.files).length;
    const uploadedFilesCount = this.filesToUpload.length;

    if (currentFilesCount + uploadedFilesCount > this.maxFiles) {
      this.errors.push(`Maximum number of files is ${this.maxFiles}`);
    }

    this.renderErrors();

    return !this.errors.length;
  }

  renderFiles() {
    const fileNames = Object.keys(this.files);

    this.listTarget[fileNames.length ? 'removeAttribute' : 'setAttribute']('hidden', 'hidden');
    this.listTarget.innerHTML = '';

    const fileItems = fileNames.map((fileName) => {
      const internalFileName = this.files[fileName];

      return `
        <li>
          ${fileName}
          <input type="hidden" name="${this.name}" value="${internalFileName}">
          <button
            class="file-upload__remove"
            type="button"
            aria-label="Remove file"
            data-key="${fileName}"
            data-action="click->file-upload#onRemoveFile"
          >
            &nbsp;
          </button>
        </li>
      `;
    }).join('');

    this.listTarget.innerHTML = fileItems;
  }

  renderErrors() {
    this.errorsTarget[this.errors.length ? 'removeAttribute' : 'setAttribute']('hidden', 'hidden');
    this.errorsTarget.setAttribute('aria-live', 'polite');

    this.errorsTarget.innerHTML = this.errors.map((error) => (
      `<li>${error}</li>`
    ));
  }

  // eslint-disable-next-line class-methods-use-this
  onDragend(event) {
    prevent(event);
  }

  // eslint-disable-next-line class-methods-use-this
  onDragstart(event) {
    prevent(event);
  }

  // eslint-disable-next-line class-methods-use-this
  onDrag(event) {
    prevent(event);
  }

  onDragleave(event) {
    prevent(event);
    this.toggleDragClass(false);
  }

  onDragenter(event) {
    prevent(event);
    this.toggleDragClass(true);
  }

  onDragover(event) {
    prevent(event);
    this.toggleDragClass(true);
  }

  toggleDragClass(state) {
    this.dropZoneTarget.classList[state ? 'add' : 'remove']('is-drag-over');
  }

  uploadFiles() {
    const formData = new FormData();

    this.filesToUpload.forEach((file) => {
      formData.append('files[]', file);
    });

    this.toggleLoadingState(true);

    fetch(this.data.get('api'), {
      method: 'POST',
      headers: {
        'X-CSRF-TOKEN': csrfToken(),
        Accept: 'application/json',
      },
      body: formData,
    })
      .then((res) => res.json())
      .then((res) => {
        this.toggleLoadingState(false);

        if (typeof res.files === 'undefined') {
          this.errors.push('Files could not be uploaded, try again later.');
          this.renderErrors();
          return;
        }

        this.files = { ...this.files, ...res.files };
        this.renderFiles();
      })
      .catch(() => {
        this.toggleLoadingState(false);
        this.errors.push('Files could not be uploaded, try again later.');
        this.renderErrors();
      });
  }

  deleteFile(filename) {
    fetch(this.data.get('destroy-api').replace(':key', filename), {
      method: 'POST',
      headers: {
        'X-CSRF-TOKEN': csrfToken(),
      },
    })
      .then((res) => res.json())
      .catch(() => {
        //
      });
  }

  toggleLoadingState(loadingState) {
    this.loadingTarget[loadingState ? 'removeAttribute' : 'setAttribute']('hidden', 'hidden');
  }
}

FileUpload.targets = [
  'file',
  'list',
  'errors',
  'dropZone',
  'loading',
];

export default FileUpload;
